import type { AxiosInstance } from 'axios';
import type * as QA from './interfaces/projects.quant-analysis.js';
import type * as QF from './interfaces/projects.quant-folders.js';

export class ProjectQuantAnalysesClient {
  constructor(private authenticatedClient: AxiosInstance) {}

  uploadProjectQuantAnalysisFile = (data: QA.UploadQuantAnalysisFile.Request) => {
    const client = this.authenticatedClient;

    const formData = new FormData();

    formData.append('quantFile', data.file, data.file.name);
    formData.append('name', data.name);

    return client.post<QA.UploadQuantAnalysisFile.Response>(
      `/projects/${data.projectId}/quant-analysis${data.quantAnalysisId ? `/${data.quantAnalysisId}` : ''}/upload`,
      formData
    );
  };

  getProjectQuantAnalysis = (data: QA.GetProjectQuantAnalysis.Request) => {
    const client = this.authenticatedClient;
    // If no version number specified, call the latest version endpoint
    if (!data.versionNumber) {
      return client.get<QA.GetProjectQuantAnalysis.Response>(
        `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}`
      );
    }
    // If version number specified, call the versioned endpoint
    return client.get<QA.GetProjectQuantAnalysis.Response>(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/version/${data.versionNumber}`
    );
  };

  getProjectQuantAnalysisVersions = (data: QA.GetProjectQuantAnalysisVersions.Request) => {
    const client = this.authenticatedClient;
    return client.get<QA.GetProjectQuantAnalysisVersions.Response>(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/versions`
    );
  };

  deleteQuantAnalysis = (data: QA.DeleteQuantAnalysis.Request) => {
    const client = this.authenticatedClient;
    return client.delete(`/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}`);
  };

  reparseQuantAnalysis = (data: QA.ReparseQuantAnalysisFile.Request) => {
    const client = this.authenticatedClient;
    return client.post<QA.ReparseQuantAnalysisFile.Response>(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/reparse/${data.versionNumber}`,
      {}
    );
  };

  deleteQuantAnalysisItem = (data: QA.DeleteQuantAnalysisItem.Request) => {
    const client = this.authenticatedClient;
    return client.delete(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/items/${data.itemId}`
    );
  };

  renameQuantAnalysisItem = (data: QA.RenameQuantAnalysisItem.Request) => {
    const client = this.authenticatedClient;
    return client.patch(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/items/${data.itemId}/name`,
      {
        name: data.name,
      }
    );
  };

  updateQuantAnalysisItem = (data: QA.UpdateQuantAnalysisItem.Request) => {
    const client = this.authenticatedClient;
    return client.patch(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/items/${data.itemId}`,
      {
        name: data.name,
        description: data.description,
        tags: data.tags,
      }
    );
  };

  reparseQuantAnalysisItem = (data: QA.ReparseQuantAnalysisItem.Request) => {
    const client = this.authenticatedClient;
    return client.post<QA.ReparseQuantAnalysisItem.Response>(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/items/${data.itemId}/reparse/${data.versionNumber}`,
      {}
    );
  };

  createQuantFolder = (data: QF.CreateQuantFolder.Request) => {
    const client = this.authenticatedClient;
    const body: { name: string; versionId: number; parentId?: number } = {
      name: data.name,
      versionId: data.versionId,
    };
    if (data.parentId !== null) {
      body.parentId = data.parentId;
    }
    return client.post<QF.CreateQuantFolder.Response>(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/folders`,
      body
    );
  };

  renameQuantFolder = (data: QF.RenameQuantFolder.Request) => {
    const client = this.authenticatedClient;
    return client.put(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/folders/${data.folderId}`,
      {
        newName: data.newName,
      }
    );
  };

  deleteQuantFolder = (data: QF.DeleteQuantFolder.Request) => {
    const client = this.authenticatedClient;
    return client.delete(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/folders/${data.folderId}`
    );
  };

  moveItemsToQuantFolder = (data: QF.MoveItemsToQuantFolder.Request) => {
    const client = this.authenticatedClient;
    return client.put(`/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/items/move`, {
      folderId: data.folderId,
      itemIds: data.itemIds,
    });
  };

  moveFolderToQuantFolder = (data: QF.moveFolderToQuantFolder.Request) => {
    const client = this.authenticatedClient;
    return client.put(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/folders/${data.folderId}/move`,
      {
        parentId: data.parentId,
      }
    );
  };

  getQuantDirectoryTree = async (data: QF.GetQuantDirectoryTree.Request) => {
    const client = this.authenticatedClient;
    const response = await client.get<QF.GetQuantDirectoryTree.Response>(
      `/projects/${data.projectId}/quant-analysis/${data.quantAnalysisId}/versions/${data.versionId}/tree`
    );
    return response.data;
  };

  generateQuantAnalysisChartTitle = async (data: QA.GenerateQuantAnalysisChartTitle.Request) => {
    const client = this.authenticatedClient;
    return await client.post<QA.GenerateQuantAnalysisChartTitle.Response>(
      `/projects/${data.projectId}/quant-analysis/generate-chart-title`,
      {
        title: data.title,
        rowPaths: data.rowPaths,
        columnPaths: data.columnPaths,
        chartType: data.chartType,
        filters: data.filters,
        selection: data.selection,
      }
    );
  };
}
